"use client"

import { useMemo, useState } from "react"
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, Save, X, PlusCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    useGetCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
} from "@/services/categoryApi"
import { useSelector } from "react-redux"
import { RootState } from "@/redux/store"
import { useToast } from "@/components/ui/use-toast"
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog"
import { useForm } from "react-hook-form"
import { ErrorType } from "@/types"
import { cn } from "@/lib/utils"
import useDocumentTitle from "@/components/ui/common/document-title"
import EmptyCategory from "@/assets/empty_categoery.png"

type CategoryFormData = {
    name: string
    parent_category_id: number
}

export default function CategoriesPage() {
    useDocumentTitle("Categories")
    const { toast } = useToast()
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0

    const [activeType, setActiveType] = useState<"facility" | "product" | "member" | "membership" | "credit_add_ons">("facility")
    const [isAddingCategory, setIsAddingCategory] = useState(false)
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})
    const [editingCategory, setEditingCategory] = useState<number | null>(null)
    const [addingSubcategoryTo, setAddingSubcategoryTo] = useState<number | null>(null)

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null)

    const { data: categoryData, isLoading } = useGetCategoriesQuery({
        org_id: orgId,
        query: `type=${activeType}&load_subcategories=true&sort_key=id&sort_order=desc`
    })

    const [createCategory] = useCreateCategoryMutation()
    const [updateCategory] = useUpdateCategoryMutation()
    const [deleteCategory] = useDeleteCategoryMutation()

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CategoryFormData>({
        defaultValues: { name: "", parent_category_id: 0 }
    })

    const categories = useMemo(() => Array.isArray(categoryData?.data) ? categoryData.data : [], [categoryData])

    const handleTypeChange = (value: string) => {
        setActiveType(value as "facility" | "product" | "member" | "membership" | "credit_add_ons")
        setIsAddingCategory(false)
        setEditingCategory(null)
        setAddingSubcategoryTo(null)
        reset()
    }

    const toggleCategory = (categoryId: number) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }))
    }

    const onSubmit = async (data: CategoryFormData) => {
        if (!data.name.trim()) return

        try {
            if (editingCategory) {
                await updateCategory({
                    id: editingCategory,
                    name: data.name.trim(),
                    type: activeType,
                    org_id: orgId
                }).unwrap()
                setEditingCategory(null)
                toast({ title: "Category updated successfully", variant: "success" })
            } else if (addingSubcategoryTo) {
                await createCategory({
                    name: data.name.trim(),
                    type: activeType,
                    parent_category_id: addingSubcategoryTo,
                    org_id: orgId
                }).unwrap()
                setAddingSubcategoryTo(null)
                toast({ title: "Subcategory created successfully", variant: "success" })
            } else {
                await createCategory({
                    name: data.name.trim(),
                    type: activeType,
                    parent_category_id: 0,
                    org_id: orgId
                }).unwrap()
                setIsAddingCategory(false)
                toast({ title: "Category created successfully", variant: "success" })
            }
            reset()
        } catch (error) {
            const typedError = error as ErrorType
            toast({
                variant: "destructive",
                title: typedError.data?.detail || typedError.data?.message || "An error occurred"
            })
        }
    }

    const startEdit = (category: any) => {
        setEditingCategory(category.id)
        setValue("name", category.name)
        setAddingSubcategoryTo(null)
        setIsAddingCategory(false)
    }

    const cancelAction = () => {
        setEditingCategory(null)
        setAddingSubcategoryTo(null)
        setIsAddingCategory(false)
        reset()
    }

    const confirmDelete = async () => {
        if (!categoryToDelete) return
        try {
            await deleteCategory(categoryToDelete).unwrap()
            toast({ title: "Deleted successfully", variant: "success" })
        } catch {
            toast({ title: "Failed to delete", variant: "destructive" })
        }
        setDeleteConfirmOpen(false)
        setCategoryToDelete(null)
    }

    return (
        <div className="p-5">
            <Card className="w-full max-w-3xl mx-auto">
                <CardHeader className="py-3">
                    <CardTitle className="text-lg font-medium">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            {/* <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Category Type:</span> */}
                            <Select value={activeType} onValueChange={handleTypeChange}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="facility">Facility</SelectItem>
                                    <SelectItem value="product">Product</SelectItem>
                                    <SelectItem value="member">Members</SelectItem>
                                    <SelectItem value="membership">Membership</SelectItem>
                                    <SelectItem value="credit_add_ons">Credit Add-ons</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => {
                                setIsAddingCategory(true)
                                setEditingCategory(null)
                                setAddingSubcategoryTo(null)
                                reset()
                            }}
                        >
                            <Plus className="mr-1 h-4 w-4" /> Add Category
                        </Button>
                    </div>

                    {isAddingCategory && (
                        <form onSubmit={handleSubmit(onSubmit)} className="mb-4 p-2 border rounded-md bg-muted/20">
                            <div className="flex items-center gap-2">
                                <Input placeholder="Category name" {...register("name", { required: true })} className="flex-1" autoFocus />
                                <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90"><Save className="h-4 w-4" /></Button>
                                <Button onClick={cancelAction} size="sm" variant="ghost"><X className="h-4 w-4" /></Button>
                            </div>
                        </form>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : categories.length === 0 && !isAddingCategory ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <img src={EmptyCategory} alt="No categories" width={160} height={160} className="mb-4 opacity-70" />
                            <h3 className="text-sm font-medium text-muted-foreground">No Categories Found</h3>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {categories.filter(c => !c.parent_category_id).map((category: any) => (
                                <div key={category.id} className="border rounded-md overflow-hidden">
                                    <div className="flex items-center justify-between p-3 bg-muted/10">
                                        <div className="flex items-center gap-2 flex-1">
                                            {/* Enable hierarchy for all generic categories if data exists */}
                                            {activeType === "facility" && category.subcategories && (
                                                <Button variant="ghost" size="sm" className="p-0 h-8 w-8" onClick={() => toggleCategory(category.id)}>
                                                    {expandedCategories[category.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                </Button>
                                            )}
                                            {activeType !== "facility" && <div className="w-8" />}

                                            {editingCategory === category.id ? (
                                                <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex gap-2">
                                                    <Input {...register("name", { required: true })} className="h-8" autoFocus />
                                                    <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                    <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                </form>
                                            ) : (
                                                <span className="font-medium">{category.name}</span>
                                            )}
                                        </div>

                                        {editingCategory !== category.id && (
                                            <div className="flex items-center gap-1">
                                                {activeType === "facility" && (
                                                    <>
                                                        <Badge variant="outline" className="bg-muted text-xs">
                                                            {category.subcategories?.length || 0}
                                                        </Badge>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-primary" onClick={() => {
                                                            setAddingSubcategoryTo(category.id)
                                                            setExpandedCategories(prev => ({ ...prev, [category.id]: true }))
                                                            setEditingCategory(null)
                                                            setIsAddingCategory(false)
                                                            reset()
                                                        }}>
                                                            <PlusCircle className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(category)}>

                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => {
                                                    setCategoryToDelete(category.id)
                                                    setDeleteConfirmOpen(true)
                                                }}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Subcategories */}
                                    {activeType === "facility" && expandedCategories[category.id] && (
                                        <div className="bg-muted/5 divide-y">
                                            {addingSubcategoryTo === category.id && (
                                                <form onSubmit={handleSubmit(onSubmit)} className="p-2 pl-10 flex gap-2">
                                                    <Input placeholder="Subcategory name" {...register("name", { required: true })} className="h-8 flex-1" autoFocus />
                                                    <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                    <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                </form>
                                            )}
                                            {category.subcategories?.map((sub: any) => (
                                                <div key={sub.id} className="flex items-center justify-between p-2 pl-10">
                                                    {editingCategory === sub.id ? (
                                                        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex gap-2">
                                                            <Input {...register("name", { required: true })} className="h-8 flex-1" autoFocus />
                                                            <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                            <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                        </form>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm">{sub.name}</span>
                                                            <div className="flex items-center gap-1">
                                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(sub)}>
                                                                    <Edit className="h-3.5 w-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => {
                                                                    setCategoryToDelete(sub.id)
                                                                    setDeleteConfirmOpen(true)
                                                                }}>
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <ConfirmationDialog
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category? This action cannot be undone."
                confirmTitle="Delete"
            />
        </div>
    )
}
